import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, LogOut, Bookmark, GitCompare, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const initialFor = (email) => (email || '?').trim().charAt(0).toUpperCase();

/**
 * Compact navbar account widget.
 * - Logged out: shows a sharp-edged "Sign in" link.
 * - Logged in: shows a square initial-avatar that opens a dropdown
 *   with Saved / Compare / Sign out.
 */
const AccountMenu = ({ variant = 'desktop' }) => {
  const { user, isAuthenticated, signOut } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey   = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleSignOut = async () => {
    try { await signOut(); toast.success('Signed out.'); setOpen(false); navigate('/'); }
    catch { toast.error('Sign out failed.'); }
  };

  if (!isAuthenticated) {
    if (variant === 'mobile') {
      return (
        <div data-testid="account-menu-mobile-loggedout" className="grid grid-cols-2 gap-0 border-b border-neutral-200">
          <Link to="/login" data-testid="mobile-account-signin" className="border-r border-neutral-200 px-5 py-4 label-overline">Sign in</Link>
          <Link to="/signup" data-testid="mobile-account-signup" className="px-5 py-4 label-overline">Sign up</Link>
        </div>
      );
    }
    return (
      <Link
        to="/login"
        data-testid="navbar-signin-link"
        className="giwahs-nav-control flex h-10 items-center justify-center whitespace-nowrap rounded-full px-4 text-xs uppercase tracking-wider"
      >
        Sign in
      </Link>
    );
  }

  if (variant === 'mobile') {
    return (
      <div data-testid="account-menu-mobile-loggedin" className="border-b border-neutral-200">
        <div className="px-5 py-3">
          <div className="label-overline text-neutral-500">Signed in as</div>
          <div className="mt-1 text-sm font-mono-data break-all">{user.email}</div>
        </div>
        <button
          data-testid="mobile-account-signout"
          onClick={handleSignOut}
          className="block w-full border-t border-neutral-200 px-5 py-4 text-left label-overline"
        >
          Sign out
        </button>
      </div>
    );
  }

  return (
    <div data-testid="account-menu" ref={ref} className="relative">
      <button
        data-testid="account-menu-trigger"
        onClick={() => setOpen(v => !v)}
        className="giwahs-nav-control flex h-10 items-center gap-2 rounded-full px-3"
        aria-haspopup="menu"
        aria-expanded={open}
        title={user.email}
      >
        <span className="grid h-6 w-6 place-items-center rounded-full bg-white font-mono-data text-[11px] text-black">
          {initialFor(user.email)}
        </span>
        <ChevronDown size={12} strokeWidth={1.5} />
      </button>

      {open && (
        <div
          data-testid="account-menu-dropdown"
          role="menu"
          className="absolute right-0 top-[calc(100%+6px)] w-64 border border-black bg-white shadow-[6px_6px_0_0_#000]"
        >
          <div className="border-b border-neutral-200 px-4 py-3">
            <div className="label-overline text-neutral-500">Signed in as</div>
            <div className="mt-1 text-sm font-mono-data break-all">{user.email}</div>
          </div>
          <Link
            to="/saved"
            data-testid="account-menu-saved"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 border-b border-neutral-200 px-4 py-3 text-sm hover:bg-neutral-50"
            role="menuitem"
          >
            <Bookmark size={14} strokeWidth={1.5} /> Saved suppliers
          </Link>
          <Link
            to="/compare"
            data-testid="account-menu-compare"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 border-b border-neutral-200 px-4 py-3 text-sm hover:bg-neutral-50"
            role="menuitem"
          >
            <GitCompare size={14} strokeWidth={1.5} /> Compare list
          </Link>
          <Link
            to="/rfq"
            data-testid="account-menu-rfq"
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 border-b border-neutral-200 px-4 py-3 text-sm hover:bg-neutral-50"
            role="menuitem"
          >
            <User size={14} strokeWidth={1.5} /> New RFQ
          </Link>
          <button
            data-testid="account-menu-signout"
            onClick={handleSignOut}
            role="menuitem"
            className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm hover:bg-black hover:text-white"
          >
            <LogOut size={14} strokeWidth={1.5} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
};

export default AccountMenu;
